import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

/**
 * Two-column legal page layout used for Privacy Policy and Terms of Service.
 * Sections prop: [{ id, title, content }]
 */
export default function LegalPageLayout({ title, lastUpdated, sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/60 to-white">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-10 md:pt-20 md:pb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1]" style={{ fontFamily: 'Manrope' }}>
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-4 text-sm text-slate-500 font-mono">
              Last updated: {lastUpdated}
            </p>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="py-8 md:py-12 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-12">
            <aside className="md:w-64 flex-shrink-0">
              <nav className="md:sticky md:top-24">
                <ul className="space-y-1.5 border-l border-slate-200">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={`block text-[13px] leading-snug py-1 pl-4 -ml-px border-l transition-colors ${
                          activeId === s.id
                            ? 'border-blue-600 text-blue-600 font-medium'
                            : 'border-transparent text-slate-500 hover:text-slate-900'
                        }`}
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <div className="flex-1 max-w-3xl">
              {sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-20 mb-14">
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-5" style={{ fontFamily: 'Manrope' }}>
                    {s.title}
                  </h2>
                  <div className="text-base text-slate-600 leading-relaxed space-y-4">
                    {s.content}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
